import useSWRMutation from 'swr/mutation'
import {
  type SupplierIntegrationSettingRequestDto,
  type SupplierIntegrationSettingDto
} from '@amici/myamici-integrations-client'
import useApi from '../../common/hooks/useApi'
import useSWR from 'swr'
import { useCallback } from 'react'
import ReactGA from 'react-ga4'

interface UseSupplierIntegrationsSettingsHook {
  data: SupplierIntegrationSettingDto | undefined
  update: (
    supplierSettingsData: SupplierIntegrationSettingRequestDto
  ) => Promise<SupplierIntegrationSettingDto | undefined>
  isLoading: boolean
  isMutating: boolean
  isValidating: boolean
  hasData: boolean
  loadingError: any
  mutationError: any
}

function useSupplierIntegrationsSettings (
  supplierId?: string
): UseSupplierIntegrationsSettingsHook {
  const { suppliersApi, fetcher } = useApi()

  const {
    data,
    isLoading,
    isValidating,
    error: loadingError
  } = useSWR(
    supplierId + '_advanced_settings',
    async () =>
      await fetcher(suppliersApi.getSupplierSettings, { id: supplierId ?? '' })
  )

  const {
    trigger,
    isMutating,
    error: mutationError
  } = useSWRMutation(
    supplierId + '_advanced_settings',
    async (_, { arg }: { arg: SupplierIntegrationSettingRequestDto }) =>
      await fetcher(suppliersApi.createOrModifySupplierSettings, {
        id: supplierId ?? '',
        supplierIntegrationSettingRequestDto: arg
      }),
    { revalidate: false }
  )

  const update = useCallback(
    async (
      supplierSettingsData: SupplierIntegrationSettingRequestDto
    ): Promise<SupplierIntegrationSettingDto | undefined> => {
      if (!supplierId) {
        return
      }
      const result = await trigger(supplierSettingsData)

      ReactGA.event('submit_form', {
        form_id: 'supplier_settings_form',
        supplier_id: supplierId
      })

      return result
    },
    [supplierId, trigger]
  )

  const hasData = !!data?.orderIntegrationType

  return {
    data,
    update,
    isLoading,
    isMutating,
    isValidating,
    hasData,
    loadingError,
    mutationError
  }
}

export default useSupplierIntegrationsSettings
